import OlMap from '@m1/ol-map';
import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { FaMapMarker } from 'react-icons/fa';
import { RiImageCircleLine, RiCloseLine, RiExternalLinkLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';

import styles from './useIoTHubEvent.module.scss';

import RetryImage from '@/components/ui/RetryImage';
import dayjs from '@/libs/dayjs';
import EventEmitter from '@/libs/EventEmitter';

const cx = classNames.bind(styles);

const MarkerContent = ({ data, color }) => {
  const [isShowInfo, setShowInfo] = useState(false);

  const toggle = () => {
    setShowInfo(!isShowInfo);
  };

  return (
    <div className={cx('container')}>
      {isShowInfo && (
        <div className={cx('panel')}>
          <div className={cx('line')} />
          <div className={cx('header')}>
            <div className={cx('time')}>{dayjs(data.capturedAt).format('HH:mm:ss')}</div>
            <div className={cx('close')} onClick={toggle}>
              <RiCloseLine size={14} color="white" />
            </div>
          </div>
          <a href={data.originUrl} target="_blank" rel="noopener noreferrer" className={cx('link')}>
            <RetryImage src={data.thumbnailUrl} alt={data.capturedAt} />
            <div className={cx('overlay')}>
              <RiExternalLinkLine size={16} color="white" />
            </div>
          </a>
        </div>
      )}
      <div className={cx('marker')} onClick={toggle}>
        <FaMapMarker size={24} color={color} />
        <RiImageCircleLine size={12} color="white" className={cx('icon')} />
      </div>
    </div>
  );
};

const useIoTHubEvent = (robot, type) => {
  const robotOptionsPhoto = useSelector((state) => state.robotOptions[robot.id].photo);
  const robotOptionsPhotoRef = useRef(robotOptionsPhoto);

  useMountEffect(() => {
    const token = EventEmitter.subscribe(`${robot.id}/event/showPhoto`, (data) => {
      const element = type === 'sheco' ? getShecoMarkerElement(data) : getMarkerElement(data, robot.color);
      const position = {
        lat: data.position.latitude,
        lng: data.position.longitude,
      };

      OlMap.photo.add(robot.id, data.imageId, element, position, robotOptionsPhotoRef.current.visible);
    });

    return () => {
      EventEmitter.unsubscribe(token);
      OlMap.photo.remove(robot.id);
    };
  });

  useUpdateEffect(() => {
    robotOptionsPhotoRef.current = robotOptionsPhoto;
  }, [robotOptionsPhoto]);

  useUpdateEffect(() => {
    OlMap.photo.setVisible(robot.id, robotOptionsPhoto.visible);
  }, [robotOptionsPhoto.visible]);

  const getMarkerElement = (data, color) => {
    const content = document.createElement('div');
    createRoot(content).render(<MarkerContent data={data} color={color} />);
    return content;
  };

  const getShecoMarkerElement = (data) => {
    const content = document.createElement('div');
    createRoot(content).render(<MarkerContent data={data} color={data.isDetected ? 'red' : 'blue'} />);
    return content;
  };
};

export default useIoTHubEvent;
